<!-- 
Panel allowing download, upload or preview of the invoice template.

Also allows the user to download a Word mail merge source document to assist with invoice template authoring.
-->
<template>
  <v-card flat class="ma-4">
    <v-card-text class="pt-2 pl-0">
      <h4 class="title mb-4 mt-0">
        Invoice template
        <our-user-guide-button topic="levy-collection-setup" section="invoice-template" in-title/>
      </h4>
      <p>
        The invoice template is used to generate the levy invoice PDF that the system will send to providers.
        <template v-if="canEdit">
          To change the template:
        </template>
      </p>
      <ul v-if="canEdit">
        <li>Download the template using the button below</li>
        <li>Open it in MS Word</li>
        <li>Make your changes</li>
        <li>Upload the revised template using the button below</li>
      </ul>
      <v-btn class="mr-2 mt-3 pl-3 ml-0 default" @click="download">
        <img src="@/assets/word-logo.svg" alt="Word document" height="22" class="mr-2">
        Download
      </v-btn>
      <v-btn :disabled="!canEdit" class="mt-3" @click="showUploadInvoiceTemplateDialog=true">
        <v-icon class="mr-2">publish</v-icon>
        Upload
      </v-btn>
      <v-btn 
        :to="`/levy-collections/${levyCollectionId}/invoice-template-preview`" class="mt-3"
        target="_blank"
      >
        <v-icon class="mr-2">visibility</v-icon>
        Preview
      </v-btn>
      <invoice-template-upload-dialog 
        v-model="showUploadInvoiceTemplateDialog" 
        :levy-collection-id="levyCollectionId" 
        type="invoice" description="invoice PDF template"
      />

      <h4 class="subheading mb-3 mt-4">Mail merge source</h4>
      <p>
        To assist in editing the invoice template you can download a mail merge source spreadsheet. This will let MS Word
        show you a list of fields that can be inserted into the template. You should only need to do this once. To set this up:
      </p>
      <ul>
        <li>Download the mail merge source spreadsheet using the button below</li>
        <li>Open MS Word</li>
        <li>Choose <code>Mailings > Select recipients > Use an existing list</code></li>
        <li>Select the mail merge source spreadsheet and click OK</li>
        <li>You will now be able to choose from a list of available fields by clicking <code>Mailings > Insert merge field</code></li>
      </ul>
      <v-btn class="mr-2 mt-3 pl-3 ml-0 default" @click="downloadMailMergeSource">
        <img src="@/assets/excel-logo.svg" alt="Excel document" height="22" class="mr-2">
        Download
      </v-btn>

    </v-card-text>
  </v-card>
</template>

<script>
import InvoiceTemplateUploadDialog from "@/components/levy-collection-setup/InvoiceTemplateUploadDialog.vue";
import lookupMixins from "@/mixins/lookup.mixins";
import * as action from "@/store/levy-collections/action-types";
import * as claim from "@/store/claim-types";
import { SHOW_NOTIFICATION } from "@/store/notifications/mutation-types";

export default {
  components: { InvoiceTemplateUploadDialog },
  mixins: [lookupMixins],
  props: {
    levyCollectionId: {
      required: true,
      type: Number
    }
  },
  data() {
    return {
      menu: false,
      invalidFile: false,
      formData: null,
      showUploadInvoiceTemplateDialog: false
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollections.pdfTemplate;
    },
    canEdit() {
      return this.$store.getters.claims.includes(claim.LEVY_COLLECTION_CONFIGURE_INVOICE) && !this.isFinalised;
    },
    isFinalised() {
      return this.$store.state.levyCollections.view.data.status == "Finalised";
    }
  },
  methods: {
    download() {
      this.$store.commit(SHOW_NOTIFICATION, "Your download will commence shortly.");
      window.location.href = `/api/levy-collections/${this.levyCollectionId}/invoice-template`;
    },
    downloadMailMergeSource() {
      this.$store.commit(SHOW_NOTIFICATION, "Your download will commence shortly.");
      window.location.href = "/api/levy-collections/mail-merge-source";
    },
    upload(file) {
      this.invalidFile =
        file && file.name && !file.name.toLowerCase().endsWith("doc") && !file.name.toLowerCase().endsWith("docx");
      if (!this.invalidFile) {
        this.formData = new FormData();
        this.formData.append("document", file);
        this.save();
      }
    },
    async save() {
      await this.$store.dispatch(action.INVOICE_TEMPLATE_UPLOAD, {
        levyCollectionId: this.levyCollectionId,
        formData: this.formData
      });
    }
  }
};
</script>
